import { useState } from 'react';

import { datadogLogs } from '@datadog/browser-logs';
import { tracking } from '@iwoca/frontend-tracking-library';

import { fetchGetEmailAvailability } from '../../api/lendingApi/lendingApiQueries';
import { getAmplitudeDefaultEventProperties } from '../../utils/amplitudeEvents';

type TDuplicateEmail = {
  email?: string;
  status: boolean;
};

export const useDuplicateEmail = () => {
  const [duplicateEmail, setDuplicateEmail] = useState<TDuplicateEmail>({
    email: '',
    status: false,
  });

  const getEmailAvailability = async (email: string) => {
    if (!email) return;

    try {
      const emailAvailability = await fetchGetEmailAvailability(email);

      if (!emailAvailability) {
        tracking.ampli.duplicateEmail({
          ...getAmplitudeDefaultEventProperties(),
        });
      }

      return emailAvailability;
    } catch (error: unknown) {
      datadogLogs.logger.error(
        'Fetch email availability error',
        { email },
        error as Error,
      );

      setDuplicateEmail({ email, status: false });
    }
  };

  const checkEmailOnBlur = async ({
    email,
    validateEmail,
    assumeAvailable,
  }: {
    email: string;
    validateEmail: () => Promise<boolean>;
    assumeAvailable?: boolean;
  }) => {
    if (duplicateEmail.email === email) return;

    // Prevent failed network call with invalid email format
    if (!(await validateEmail())) return;

    const emailAvailable =
      assumeAvailable || (await getEmailAvailability(email));

    if (emailAvailable === undefined) return;

    if (duplicateEmail.status && emailAvailable) {
      tracking.ampli.duplicateEmailChangedToNonDuplicate();
    }

    setDuplicateEmail({ email, status: !emailAvailable });
  };

  const checkEmailOnSubmit = async (email: string) => {
    if (!duplicateEmail.status) return true;

    if (duplicateEmail.email === email) return false;

    if (!(await getEmailAvailability(email))) return false;

    setDuplicateEmail({ email, status: false });
    return true;
  };

  return {
    duplicateEmail,
    setDuplicateEmail,
    checkEmailOnBlur,
    checkEmailOnSubmit,
  };
};
